import { Component, OnInit, QueryList, ViewChildren, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';
import { FormControl } from '@angular/forms';
import { NavigationEnd } from '@angular/router';
import { HeaderComponent } from '../../header/header.component';
import { APP_CONFIG } from '@core-app/app.config';
import { CampusService } from '@core-app/services/campus.service';

@Component({
  selector: 'core-top-navigation',
  templateUrl: './top-nav.component.html',
  standalone: false,
  host: {
    '(window:resize)': 'onResize($event)',
    '(keydown.escape)': 'onEsc()',
  },
})
export class TopNavComponent extends HeaderComponent implements OnInit {
  private campusService = inject(CampusService);

  @ViewChildren(BsDropdownDirective)
  dropdowns?: QueryList<BsDropdownDirective>;

  pillars$!: Observable<any>;

  delayedLoaded = false;
  activeMenuTab = new FormControl(0);
  fragment?: string;
  resizeTimeout: any;
  clickIndexNo?: void | number;
  navigationOrder: 'name' | 'displayOrder' = 'displayOrder';
  menuStyle = (APP_CONFIG as any).navigation?.style || 'flyout';
  selectedCampus!: number;

  // Menu variables
  content$?: Observable<any>;
  public openDropdown: any;

  onResize(event: Event) {
    if (this.isBrowser) {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        this.isDesktopCheck(event.target);
      }, 200);
    }
  }

  // Close dropdown menu on escape click

  onEsc() {
    if (this.dropdownIsOpen && this.dropdowns) {
      this.dropdowns.forEach((dropdown: BsDropdownDirective) => {
        // dropdown from BsDropdownDirective is private so assigning it to public var to allow us to use _elementRef below
        this.openDropdown = dropdown;
        // Check if current iteration is our opened one
        if (this.openDropdown._elementRef.nativeElement.classList.contains('show')) {
          // Add focus manually back on navbar link
          this.openDropdown._elementRef.nativeElement.firstChild.focus();
        }
        this.openDropdown.hide();
      });
    }
  }

  selectedMobNavLinkIndex: number | undefined;
  isDesktop = true;
  isCategoryNav?: boolean;
  resetMobileScroll?: boolean;
  isMobileNavFooter = true; // Boolean to use a fixed footer element for cta at bottom of mobile nav

  contactEnabled!: boolean;
  dropdownIsOpen!: boolean;

  override ngOnInit(): void {
    super.ngOnInit();
    this.content$ = this.client.getPageCategories();

    this.api.zoneChangeEmit.subscribe(() => {
      this.pillars$ = this.client.getPillars();
    });

    // Subscribe to campus changes and update logos URL
    this.subscriptions.push(
      this.campusService.selectedCampus$.subscribe(campus => {
        if (campus) {
          this.updateLogoUrl();
          this.selectedCampus = campus;
        }
      }),
    );

    this.subscriptions.push(
      this.settings.showBenefitCategories().subscribe((state: any) => {
        this.isCategoryNav = state;
      }),
    );

    this.subscriptions.push(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.updateLogoUrl();
        }
      }),
    );

    if (this.isBrowser) {
      this.isDesktopCheck(window);
    }

    this.subscriptions.push(
      this.settings.isContactEnabled().subscribe((state: any) => {
        this.contactEnabled = state;
      }),
    );
    this.navigationOrder = (APP_CONFIG as any).navigation['navigationOrder'] ?? 'name';
  }

  onOpenChange(isOpen: boolean, index: number): void {
    const overlayElem = this.documentService.querySelector('.page-overlay');

    if (isOpen && overlayElem) {
      this.renderer.addClass(overlayElem, 'page-overlay--is-active');
      this.clickIndexNo = index;
    } else if (this.clickIndexNo === index) {
      // only close page overlay when the same link is clicked twice
      this.renderer.removeClass(overlayElem, 'page-overlay--is-active');
    }
  }

  onDropdownOpen() {
    return (this.dropdownIsOpen = true);
  }

  menuStateCheck(menuState: boolean, event?: Event) {
    this.closeChildNav(event);
    if (this.dropdowns) {
      this.hideDropdowns();
    }
    const tmpOverflow = menuState ? 'revert' : 'hidden';
    if (this.documentService.body) {
      this.documentService.body.style.setProperty('overflow-y', tmpOverflow, 'important');
    }
  }

  toggleMobileMenu(evt?: Event) {
    this.isCollapsed = !this.isCollapsed;
    this.menuStateCheck(this.isCollapsed, evt);

    // Ensures the logos is correct colour when mobile nav open/closed for transparent nav on
    if (this.isTransparentNav) {
      if (!this.darkMode) {
        this.logoUrl = !this.isCollapsed ? this.logoInvertedUrl : this.logoTransparentUrl;
      } else {
        // only need to show white logos in dark mode when transparent nav on
        this.logoUrl = this.logoTransparentUrl;
      }
    }
  }

  closeChildNav(event?: Event) {
    // Setting value to number that isn't the index to remove is-active class on mobile benefits submenu
    this.selectedMobNavLinkIndex = 99;
    // Preventing click propagation. This keeps the mobile Benefits accordion open when a submenu is closed
    if (event) {
      event.stopPropagation();
    }
    // Add main scrollbar back if required
    this.resetMobileScroll = false;
  }

  setTopNavActiveLinkState(page: any) {
    let setActiveState = false;
    // If page has children and child page is current page, set active link on parent page link
    if (page.children && page.children.length > 0) {
      for (const pageChild of page.children) {
        const childPageRoute = '/' + pageChild.url;
        if (this.router.url === childPageRoute) {
          setActiveState = true;
        }
      }
    } else {
      // No child pages
      // Home page check as this.router.url and page.url won't match
      if (this.router.url === '/' && page.url === 'default') {
        setActiveState = true;
      } else {
        // Set link to active if link page and current page match for page that aren't home and don't have children
        const pageRoute = '/' + page.url;
        setActiveState = this.router.url === pageRoute;
      }
    }
    return setActiveState;
  }

  setDropdownActiveLinkState(page: any) {
    // Set dropdown (child) link to active if link page and current page match
    const pageRoute = '/' + page.url;
    return this.router.url === pageRoute;
  }

  isDesktopCheck(elem: any) {
    if (elem.innerWidth >= 992) {
      if (!this.isCollapsed) {
        this.isCollapsed = true;
        this.menuStateCheck(true, undefined);
      }
      this.isDesktop = true;
    } else {
      this.isDesktop = false;
    }

    this.setMobChildNavHeight();
  }

  setMobChildNavHeight() {
    // 100vh fix for iOS and Android
    // We effectively calculate the left over real-estate for the scrolling sub-menu of pages.
    // Yes I know, I know...
    // Current value is 374px
    // 91px - Header bar
    // 118px - Search section (includes margin)
    // 70px - 'Back' link
    // 94px - Country Selector

    // Changing space needed at bottom of mob nav if we need it for a cta button
    const offset = this.isMobileNavFooter ? 374 : 320;
    const vh = this.windowService.innerHeight - offset;
    this.documentService.documentElement?.style.setProperty('--vhcustom-child-menu', `${vh}px`);

    // LifeMoment height fix for smaller screens
    // See above
    // 90px Header Bar
    // 67px Navigation Bar
    // 60px Padding (30px top/bottom)
    // let vhlm = window.innerHeight - 90 - 67 - 60
    // this.documentService.documentElement.style.setProperty('--vhcustom-lifemoments', `${vhlm}px`);
  }

  // This handles changing pages. It's shared across all screen sizes.
  // 1: It will automatically close the dropdown menu on Desktop
  // 2: It will also remove the mobile styling if user navigates page on mobile
  // 3: If the menu is open on mobile it will close it
  // 6: Will navigate to correct page
  navigateToPage(page: string) {
    if (this.dropdowns) {
      this.hideDropdowns();
      this.onOpenChange(false, -1);
    }
    if (this.documentService.body) {
      this.documentService.body.style.overflowY = 'revert';
    }
    if (!this.isCollapsed) {
      this.isCollapsed = true;
    }

    if (page && page.indexOf('#') > -1) {
      // Contains HASH!
      const urlArray = page.split('#');
      this.router.navigate([urlArray[0]], { fragment: urlArray[1] });
    } else {
      // No Hash, proceed as usual
      if (page === 'default') {
        page = '';
      }
      this.router.navigate(['/' + page]);
    }
  }

  closeChildDropdown() {
    if (this.dropdowns) {
      this.hideDropdowns();
      this.onOpenChange(false, -1);
    }
  }

  override updateLogoUrl() {
    // Use the selected campus from CampusService if available, otherwise fall back to API service zone
    const selectedCampus = this.campusService.selectedCampus;
    const serviceZone = selectedCampus || Number(this.api.getServiceZone());
    this.logoUrl = APP_CONFIG.imagery.logos![serviceZone - 1].url;
  }

  showTransparentLogo() {
    return this.isTransparentNav && this.isCollapsed && !this.isTopNavTransparentSticky;
  }

  showTransparentLogoForDarkModeViaSystemPref() {
    return (
      (this.darkMode && this.isTopNavTransparentSticky) ||
      this.settings.isSystemPreferenceDarkModeOn() ||
      (this.windowService.matchMedia && this.windowService.matchMedia('(prefers-color-scheme: dark)').matches)
    );
  }

  showDarkLogoForLightModeViaSystemPref() {
    return (
      (!this.darkMode && this.isTopNavTransparentSticky) ||
      this.settings.isSystemPreferenceLightModeOn() ||
      (this.windowService.matchMedia && this.windowService.matchMedia('(prefers-color-scheme: light)').matches) ||
      (!this.isTransparentNav && !this.darkMode)
    );
  }

  stopPropagation($event: Event) {
    return $event.stopPropagation();
  }

  hideDropdowns() {
    this.dropdowns?.forEach((dropdown: BsDropdownDirective) => {
      dropdown.hide();
    });
  }

  get isNavbarWithFlyout(): boolean | undefined {
    return (this.menuStyle === 'flyout' && !this.isIndividualMenuFlyout) || this.isIndividualMenuFlyout;
  }

  getCampusHomepageLink() {
    switch (this.campusService.selectedCampus) {
      // UHS
      case 1:
        return 'https://uh.edu/';

      // Clear Lake
      case 2:
        return 'https://www.uhcl.edu/';

      // Downtown
      case 3:
        return 'https://www.uhd.edu/';

      default:
        return '/';
    }
  }

  getCampusLogo() {
    switch (this.campusService.selectedCampus) {
      // UHS
      case 1:
        return 'assets/img/uh-houston.svg';

      // Clear Lake
      case 2:
        return 'assets/img/uh-clearlake.svg';

      // Downtown
      case 3:
        return 'assets/img/uh-downtown.svg';

      default:
        return 'assets/img/uh-houston.svg';
    }
  }
}
